import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { api } from '@/services/api';
import { usePageTitle } from '@/shared/usePageTitle';
import { useToast } from '@/shared/toast';
import { useConfirm } from '@/shared/confirm';

interface Tenant {
  id: number;
  name: string;
  slug: string;
  latitude: number;
  longitude: number;
  enabled: boolean;
}

interface TenantUser { id: string; nickname: string; email: string; approved: boolean }

export function TenantsPage(): React.JSX.Element {
  usePageTitle('Gestione Distaccamenti');
  const toast = useToast();
  const confirm = useConfirm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showUsers, setShowUsers] = useState<Tenant | null>(null);
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [newName, setNewName] = useState('');
  const [newSlug, setNewSlug] = useState('');

  const { data: tenants } = useQuery<Tenant[]>({
    queryKey: ['admin.tenants.full'],
    queryFn: async () => (await api.get('/admin/tenants')).data,
  });

  const create = useMutation({
    mutationFn: async () => {
      await api.post('/admin/tenants', {
        name: newName.trim(),
        slug: newSlug.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        latitude: 0, longitude: 0,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.tenants.full'] });
      toast.show('Distaccamento creato');
      setNewName(''); setNewSlug('');
    },
    onError: () => toast.show('Errore: slug duplicato o non valido', 'error'),
  });

  const toggleEnabled = useMutation({
    mutationFn: async (t: Tenant) => {
      await api.put(`/admin/tenants/${t.id}`, {
        name: t.name, latitude: t.latitude, longitude: t.longitude, enabled: !t.enabled,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin.tenants.full'] });
      toast.show('Stato aggiornato');
    },
  });

  async function handleDelete(t: Tenant) {
    const ok = await confirm({
      title: 'Elimina distaccamento',
      message: `Eliminare "${t.name}"? Operazione irreversibile (richiede 0 utenti).`,
      confirmLabel: 'Elimina', variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/tenants/${t.id}`);
      qc.invalidateQueries({ queryKey: ['admin.tenants.full'] });
      toast.show('Distaccamento eliminato');
    } catch {
      toast.show('Errore: distaccamento ha utenti collegati', 'error');
    }
  }

  return (
    <div className="page-container">
      <button className="btn-back" onClick={() => navigate('/dashboard')}
        style={{ marginBottom: 20 }}>← Indietro</button>

      <h3 style={{ textAlign: 'center', fontSize: 28, marginBottom: 20 }}>🏢 Gestione Distaccamenti</h3>

      {/* Form crea */}
      <div className="create-station-form">
        <h4>+ Crea Nuovo Distaccamento</h4>
        <input placeholder="Nome distaccamento (es. Frassinoro)" value={newName}
          onChange={e => setNewName(e.target.value)} />
        <input placeholder="ID univoco (es. modena_001, solo a-z 0-9 _)" value={newSlug}
          onChange={e => setNewSlug(e.target.value)} />
        <button onClick={() => create.mutate()}
          disabled={!newName || !newSlug || create.isPending}
          style={{ background: 'linear-gradient(135deg, #28a745 0%, #218838 100%)', color: 'white',
                   border: 'none', padding: '12px 20px', borderRadius: 8, fontWeight: 700, cursor: 'pointer', width: '100%' }}>
          {create.isPending ? 'Creazione…' : '✓ Crea Distaccamento'}
        </button>
      </div>

      <h4 style={{ margin: '20px 0 10px', color: '#333' }}>📋 Distaccamenti Esistenti:</h4>
      {(tenants ?? []).length === 0 && (
        <div style={{ textAlign: 'center', color: '#6c757d', padding: 40 }}>Nessun distaccamento</div>
      )}
      {(tenants ?? []).map(t => (
        <div key={t.id} className={`station-card ${!t.enabled ? 'disabled' : ''}`}>
          <div className="station-info">
            <h4>🏢 {t.name}</h4>
            <p>
              <strong>ID:</strong> {t.slug}<br />
              <strong>Coordinate:</strong> {t.latitude.toFixed(4)}, {t.longitude.toFixed(4)}
            </p>
          </div>
          <div className="station-actions">
            <button className={`btn ${t.enabled ? 'btn-enabled' : 'btn-disabled'}`}
              onClick={() => toggleEnabled.mutate(t)}>
              {t.enabled ? '✓ Abilitato' : '⏸ Disabilitato'}
            </button>
            <button className="btn btn-edit" onClick={() => setEditing(t)}>✏️ Modifica</button>
            <button className="btn btn-users" onClick={() => setShowUsers(t)}>👥 Utenti</button>
            <button className="btn btn-trash" onClick={() => handleDelete(t)}>🗑️ Elimina</button>
          </div>
        </div>
      ))}

      {editing && (
        <TenantEditModal tenant={editing} onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['admin.tenants.full'] }); setEditing(null); }} />
      )}
      {showUsers && (
        <TenantUsersModal tenant={showUsers} onClose={() => setShowUsers(null)} />
      )}
    </div>
  );
}

function TenantEditModal({ tenant, onClose, onSaved }: {
  tenant: Tenant; onClose: () => void; onSaved: () => void;
}): React.JSX.Element {
  const toast = useToast();
  const [name, setName] = useState(tenant.name);
  const [lat, setLat] = useState(String(tenant.latitude));
  const [lng, setLng] = useState(String(tenant.longitude));

  async function save() {
    try {
      await api.put(`/admin/tenants/${tenant.id}`, {
        name, latitude: parseFloat(lat) || 0, longitude: parseFloat(lng) || 0, enabled: tenant.enabled,
      });
      toast.show('Distaccamento aggiornato');
      onSaved();
    } catch {
      toast.show('Errore salvataggio', 'error');
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <h3>✏️ Modifica Distaccamento</h3>
        <div style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>ID: {tenant.slug} (non modificabile)</div>
        <div className="form-group">
          <label>Nome</label>
          <input value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Latitudine</label>
            <input value={lat} onChange={e => setLat(e.target.value)} placeholder="44.3288" />
          </div>
          <div className="form-group">
            <label>Longitudine</label>
            <input value={lng} onChange={e => setLng(e.target.value)} placeholder="10.5858" />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} className="btn-secondary">Annulla</button>
          <button onClick={save} className="btn-success">💾 Salva</button>
        </div>
      </div>
    </div>
  );
}

function TenantUsersModal({ tenant, onClose }: { tenant: Tenant; onClose: () => void }): React.JSX.Element {
  const { data: users } = useQuery<TenantUser[]>({
    queryKey: ['admin.users.byTenant', tenant.id],
    queryFn: async () => {
      // ?tenantId esplicito: dal fix "mai utenti mescolati" /admin/users senza
      // parametro ritorna SOLO il tenant del chiamante (anche per SuperAdmin) —
      // il vecchio fetch-tutto + filtro client qui tornava vuoto per gli altri
      // distaccamenti. Questa pagina e' SuperAdmin-only: il param viene onorato.
      return (await api.get('/admin/users', { params: { tenantId: tenant.id } })).data as TenantUser[];
    },
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <h3>👥 Utenti — {tenant.name}</h3>
        <div style={{ color: '#666', fontSize: 13, marginBottom: 16 }}>{users?.length ?? 0} utenti</div>
        {(users ?? []).length === 0 && (
          <div style={{ textAlign: 'center', color: '#888', padding: 20 }}>Nessun utente in questo distaccamento</div>
        )}
        {(users ?? []).map(u => (
          <div key={u.id} style={{
            padding: 12, margin: '6px 0', background: '#f9f9f9', borderRadius: 8,
            borderLeft: '4px solid #007bff',
          }}>
            <strong style={{ fontSize: 15 }}>{u.nickname}</strong>
            {!u.approved && <span style={{ marginLeft: 8, background: '#ffc107', color: '#000', padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 'bold' }}>IN ATTESA</span>}
            <div style={{ fontSize: 13, color: '#666', marginTop: 4 }}>{u.email}</div>
          </div>
        ))}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <button onClick={onClose} className="btn-secondary">Chiudi</button>
        </div>
      </div>
    </div>
  );
}
